/**
 * Three Card Poker bot. Betting is casual (a small multiple of the minimum, never more than a tenth of the stack, a Pair Plus
 * side bet now and then). The play-or-fold decision follows the standard strategy — play queen-six-four or better — with
 * weaker bots slipping on borderline hands. The bot only ever reads its own seat's view.
 */
import { parseCard } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { threeCardPokerEvaluate, type ThreeCardPokerAction, type ThreeCardPokerLegal, type ThreeCardPokerView } from './engine.js';

/** Queen-six-four or better is a play; anything weaker folds. */
export function threeCardPokerShouldPlay(hand: readonly string[]): boolean {
  const h = threeCardPokerEvaluate(hand);
  if (h.category !== 'HIGH_CARD') return true;
  const ranks = hand.map((c) => parseCard(c).rank).sort((a, b) => b - a) as [number, number, number];
  if (ranks[0] !== 12) return ranks[0] > 12;
  return ranks[1] > 6 || (ranks[1] === 6 && ranks[2] >= 4);
}

function choosePlay(view: ThreeCardPokerView, legal: ThreeCardPokerLegal, ctx: BotContext): ThreeCardPokerAction {
  if (legal.kind !== 'decide' || view.hand.length !== 3) return { type: 'fold' };
  const good = threeCardPokerShouldPlay(view.hand);
  // Easier bots misjudge the odd borderline hand.
  const slip = ctx.difficulty === 'BEGINNER' ? 0.15 : ctx.difficulty === 'NORMAL' ? 0.05 : 0;
  return { type: (chance(ctx, slip) ? !good : good) ? 'play' : 'fold' };
}

export const threeCardPokerBot: BotEngine<ThreeCardPokerView, ThreeCardPokerLegal, ThreeCardPokerAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'decide') return choosePlay(view, legal, ctx);
    return this.chooseBet!(view, legal, ctx);
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bet') return this.handleTimeout(view, legal, ctx);
    if (legal.canSitOut && chance(ctx, 0.03)) return { type: 'sitOut' };
    const me = view.seat === null ? null : view.players[view.seat];
    const multiple = 1 + ctx.rng.int(ctx.difficulty === 'BEGINNER' ? 4 : 3);
    const cap = me ? Math.max(legal.min, Math.floor(me.chips / 10)) : legal.min;
    const ante = Math.max(legal.min, Math.min(legal.min * multiple, cap, legal.max));
    const pairPlus = legal.pairPlusMax >= legal.min && chance(ctx, 0.25) ? Math.min(legal.min, legal.pairPlusMax) : 0;
    return { type: 'bet', ante, pairPlus };
  },

  evaluateState(view) {
    const me = view.seat === null ? null : view.players[view.seat];
    return me ? Math.max(0, Math.min(1, me.chips / (view.config.minBet * 200))) : 0;
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view) {
    return { type: view.phase === 'DECIDING' ? 'fold' : 'sitOut' };
  },
};
